import React, { useState } from "react";
import { Add, ArrowBack, Language, Refresh } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import CustomTextField from "../../../components/common/Textfield";
import PageCard from "./components/pages/PageCard";
import { scoreColorKey } from "./components/pages/ScoreBadge";
import { computeSEOAnalysis } from "./hooks/useSEOAnalysis";
import { useSitePageDefinitions } from "./hooks/useSitePageDefinitions";
import { useWebsiteSeoData } from "./hooks/useWebsiteSeoData";
import { EMPTY_FORM, pageLabel, toForm } from "./types";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";

const averageScore = (scores: number[]) =>
  scores.length > 0
    ? Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length)
    : 0;

const SEOWebsitePages: React.FC = () => {
  const { siteId: siteIdParam } = useParams<{ siteId: string }>();
  const siteId = Number(siteIdParam);
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canAdd = hasPermission(PERMISSIONS.CONFIGURATION_ADD);
  const [pageSearch, setPageSearch] = useState("");

  const { site, configs, loading, reload } = useWebsiteSeoData(siteId);
  const { pages: definedPages, loading: definitionsLoading } = useSitePageDefinitions(siteId);
  const isRegistrySite = definedPages.length > 0;
  const pagesLoading = loading || definitionsLoading;

  const filteredConfigs = configs.filter((config) =>
    pageLabel(config.page_name)
      .toLowerCase()
      .includes(pageSearch.toLowerCase()),
  );
  const filteredRegistryPages = definedPages.filter((page) =>
    page.label.toLowerCase().includes(pageSearch.toLowerCase()),
  );

  const pageStats = isRegistrySite
    ? {
        total: definedPages.length,
        avgScore: averageScore(
          definedPages.map((page) => {
            const config = configs.find((item) => item.page_name === page.key);
            return computeSEOAnalysis(
              config ? toForm(config) : { ...EMPTY_FORM, pageName: page.key },
            ).score;
          }),
        ),
      }
    : {
        total: configs.length,
        avgScore: averageScore(
          configs.map((config) => computeSEOAnalysis(toForm(config)).score),
        ),
      };

  const openPage = (pageName: string) =>
    navigate(`/configurations/seo-management/${siteId}/${pageName}`);

  return (
    <Box sx={{ pb: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate("/configurations/seo-management")}
        sx={{ mb: 2 }}
      >
        Back to websites
      </Button>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: "action.hover",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Language color="action" />
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={800}>
              {site?.name || "Website pages"}
            </Typography>
            <Typography color="text.secondary">
              {site?.domain || "Loading website..."}
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={4}>
          <Box textAlign="right">
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Avg Score
            </Typography>
            <Typography
              variant="h5"
              fontWeight={800}
              color={`${scoreColorKey(pageStats.avgScore)}.main`}
            >
              {pageStats.avgScore}
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Pages
            </Typography>
            <Typography variant="h5" fontWeight={800}>
              {pageStats.total}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        <CustomTextField
          size="small"
          placeholder="Search pages..."
          value={pageSearch}
          onChange={(event) => setPageSearch(event.target.value)}
          sx={{ maxWidth: 440, flexGrow: 1 }}
        />
        <Button startIcon={<Refresh />} onClick={reload} disabled={pagesLoading}>
          Refresh
        </Button>
        {!isRegistrySite && canAdd && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => openPage("new")}
            disabled={pagesLoading}
          >
            Add Page
          </Button>
        )}
      </Box>

      {pagesLoading ? (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <CircularProgress />
        </Box>
      ) : isRegistrySite ? (
        <Grid container spacing={2.5}>
          {filteredRegistryPages.map((page) => {
            const config = configs.find((item) => item.page_name === page.key);
            const score = computeSEOAnalysis(
              config ? toForm(config) : { ...EMPTY_FORM, pageName: page.key },
            ).score;
            return (
              <Grid key={page.key} size={{ xs: 12, sm: 6, md: 4 }}>
                <PageCard
                  label={page.label}
                  path={page.path}
                  lastUpdated={config?.updated_at}
                  score={score}
                  isDynamic={page.isDynamic}
                  onClick={() => openPage(page.key)}
                />
              </Grid>
            );
          })}
          {!filteredRegistryPages.length && (
            <Grid size={{ xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{ p: 6, borderRadius: 3, textAlign: "center" }}
              >
                <Typography color="text.secondary">
                  No pages match your search.
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      ) : (
        <Grid container spacing={2.5}>
          {filteredConfigs.map((config) => (
            <Grid key={config.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <PageCard
                label={pageLabel(config.page_name)}
                path={`/${config.page_name}`}
                lastUpdated={config.updated_at}
                score={computeSEOAnalysis(toForm(config)).score}
                onClick={() => openPage(config.page_name)}
              />
            </Grid>
          ))}
          {!filteredConfigs.length && (
            <Grid size={{ xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{ p: 6, borderRadius: 3, textAlign: "center" }}
              >
                <Typography color="text.secondary" sx={{ mb: 1 }}>
                  No SEO pages configured for this website.
                </Typography>
                {canAdd && (
                  <Button startIcon={<Add />} onClick={() => openPage("new")}>
                    Add the first page
                  </Button>
                )}
              </Paper>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default SEOWebsitePages;
